import { BASE_URL } from "../config/api";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Creates a new carousel slide
 * @param {FormData} formData - Form data containing carousel details and image
 * @returns {Promise<Object>} - Response data from the server
 * @throws {Error} - If the request fails
 */
export const createCarousel = async (formData) => {
  const response = await fetch(`${BASE_URL}/carousel/createCarousel`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to create carousel slide");
  }

  return await response.json();
};

/**
 * Fetches all carousel slides
 * @returns {Promise<Array>} - Array of carousel slides
 * @throws {Error} - If the request fails
 */
export const fetchAllCarousels = async () => {
  const response = await fetch(`${BASE_URL}/carousel/fetchAllCarousels`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch carousel slides");
  }

  const data = await response.json();
  return data.carousels || [];
};

/**
 * Fetches a single carousel slide by ID
 * @param {number|string} id - The ID of the carousel slide to fetch
 * @returns {Promise<Object>} - The carousel slide data
 * @throws {Error} - If the request fails
 */
export const fetchCarouselById = async (id) => {
  const response = await fetch(`${BASE_URL}/carousel/fetchCarouselById/${id}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch carousel data');
  }

  return await response.json();
};

/**
 * Updates an existing carousel slide
 * @param {number|string} id - The ID of the carousel slide to update
 * @param {FormData} formData - Form data containing updated carousel details
 * @returns {Promise<Object>} - Response data from the server
 * @throws {Error} - If the request fails
 */
export const updateCarousel = async (id, formData) => {
  const response = await fetch(`${BASE_URL}/carousel/updateCarousel/${id}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem("token");
      throw new Error("Authentication failed. Please login again.");
    }
    const error = await response.json();
    throw new Error(error.message || 'Failed to update carousel');
  }

  return await response.json();
};

/**
 * Updates the sequence numbers of multiple carousel slides
 * @param {Array} updates - Array of objects with carousel_id and carousel_sequence
 * @returns {Promise<Object>} - Response data from the server
 * @throws {Error} - If the request fails
 */
export const updateCarouselSequences = async (requestData) => {
  const response = await fetch(`${BASE_URL}/carousel/updateSequences`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update sequences");
  }

  return await response.json();
};